/**
 * Sparkline — pure-SVG miniature line chart for the dashboard productivity card.
 * Lightweight (no recharts dependency), accessible, dark-mode aware.
 *
 * props:
 *   data: Array<{ day: string, count: number }>
 *   height?: number (default 48)
 *   className?: string
 */
export default function Sparkline({ data = [], height = 48, className = "" }) {
  if (!data.length) {
    return <div className={`tb-skeleton h-12 w-full ${className}`} />;
  }
  const w = 280;
  const h = height;
  const pad = 4;
  const values = data.map((d) => d.count || 0);
  const max = Math.max(1, ...values);
  const min = 0;
  const span = max - min || 1;
  const step = (w - pad * 2) / Math.max(1, data.length - 1);

  const points = values.map((v, i) => {
    const x = pad + i * step;
    const y = pad + (h - pad * 2) * (1 - (v - min) / span);
    return [x, y];
  });

  const linePath = points.map((p, i) => (i === 0 ? `M${p[0]},${p[1]}` : `L${p[0]},${p[1]}`)).join(" ");
  const areaPath = `${linePath} L${points[points.length - 1][0]},${h - pad} L${points[0][0]},${h - pad} Z`;

  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      className={`w-full ${className}`}
      role="img"
      aria-label="14-day productivity sparkline"
    >
      <defs>
        <linearGradient id="tb-spark-fill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.28" />
          <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={areaPath} fill="url(#tb-spark-fill)" />
      <path d={linePath} fill="none" stroke="hsl(var(--primary))" strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
      {points.length > 0 && (
        <circle
          key="spark-last"
          cx={points[points.length - 1][0]}
          cy={points[points.length - 1][1]}
          r="3"
          fill="hsl(var(--primary))"
        />
      )}
    </svg>
  );
}
